import { useRef, useState, useEffect, useLayoutEffect, FC } from "react";
import cn from "classnames";
import DropdownSimple from "@components/DropdownSimple";
// import useOutsideClick from "@hooks/useOutsideClick";
import { Title2, Title3 } from "@components/new/Typo";
import ChevronDown from "@assets/icons/outline/chevron-down.svg?react";
import ChevronUp from "@assets/icons/outline/chevron-up.svg?react";
import { Loader } from "@components/new/Loader";
import { Tooltip } from "@components/new/Tooltip";
import styles from "./SelectDropdown.module.scss";

const TooltipContainer = ({
  disabledMessage,
  children,
}: {
  disabledMessage?: string;
  children?: string | JSX.Element | JSX.Element[];
}) => {
  if (disabledMessage) {
    return (
      <Tooltip action={<>{children}</>} position="top" text={disabledMessage} />
    );
  }
  return <>{children}</>;
};

function renderItemDefault({ item, search, labelOptionItem }: {
  item: any,
  search?: string,
  labelOptionItem: (item: any) => string,
  isSelected?: boolean,
}) {
  const s = labelOptionItem(item);
  const res = (search ? s.split(search) : [s])

  return (
    <Title3>
      {res.map((v: string, idx: number) => (
        <>
          {v}
          {(idx < res.length - 1) && (
            <em>{search}</em>
          )}
        </>
      ))}
    </Title3>
  )
}

function labelSelectedItemDefault(item: any) {
  return item?.value ?? "";
}

function defaultSearchFunc(
  v: any,
  searchString: string,
) {
  if (!v) return false
  return v.includes(searchString);
}

export interface SelectDropdownProps<T> {
  defaultSelectedItem?: T;
  value?: string | null;
  items: T[];
  keyExtractor: (item: T) => string;
  labelOptionItem?: (item: T | null) => string;
  renderSelectedItem?: (props: { item: T | null }) => React.ReactNode;
  labelSelectedItem?: (item: T | null) => string;
  renderItem?: (props: { item: T, search?: string, isSelected?: boolean, onClose?: () => void }) => React.ReactNode;
  ItemComponent?: FC<{ item: T, search?: string, isSelected?: boolean }>;
  SelectedItemComponent?: FC<{ item: T | null }>;
  onItemSelect: (id: string) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  description?: string;
  hasError?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  disabledMessage?: string;
  searchable?: boolean;
  searchFunc?: (item: T, search: string) => boolean;
  isLoading?: boolean,
  onOpen?: () => void,
  searchHandler?: (v: string) => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  menuPadding?: "s" | "m" | "l";
  menuQuantity?: number;
  hideChevron?: boolean;
  dropdownWidth?: string;
  inputWidth?: string;
  dropdownAlign?: "start" | "end";
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

export const DropdownItem = ({ isSelected, onClick, children }: {
  isSelected?: boolean;
  onClick?: () => void;
  children ?: React.ReactNode;
}) => (
  <div
    className={cn(
      styles.dropdownItem,
      isSelected && styles.selected,
      onClick && !isSelected && styles.clickableItem,
      false && styles.disabled,
    )}
    onClick={onClick}
  >
    {children}
  </div>
)

const SelectDropdown = <T extends object>(props: SelectDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // useOutsideClick<HTMLDivElement>(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    if (!selectedItem && props.defaultSelectedItem) setSelectedItem(props.defaultSelectedItem);
  }, [props.defaultSelectedItem])

  useEffect(() => {
    setSearchString("");
    if (isOpen) props.onOpen && props.onOpen();
  }, [isOpen])

  useEffect(() => {
    if (props.searchHandler) props.searchHandler(searchString);
  }, [searchString])

  const value = (props.value === undefined
    ? selectedItem
    : props.items.find(v => (props.value === props.keyExtractor(v)))
  ) || null;

  const handleSelect = (item: T) => {
    if (value === undefined) setSelectedItem(item);
    props.onItemSelect(props.keyExtractor(item));
    props.onBlur && props.onBlur();
    setIsOpen(false);
  };
  const isFilled = Boolean(value);
  const getItemLabel = props.labelOptionItem ?? labelSelectedItemDefault

  const items = searchString ? props.items.filter(
    (v) => (props?.searchFunc
      ? props?.searchFunc(v, searchString)
      : defaultSearchFunc(getItemLabel(v), searchString)
    )
  ) : props.items;

  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const { offsetWidth } = dropdownRef.current;
      setDropdownWidth(offsetWidth);
    }
  }, [dropdownRef.current, props.value]);

  const showError = props.hasError && props.errorMessage;
  const showFooter = showError || props.description;
  const SelectedItemComponent = props.SelectedItemComponent;

  return (
    <div
      className={cn(
        styles.container,
        isFilled && styles.active,
        props.hasError && styles.error,
        props.disabled && styles.disabled,
      )}
      ref={dropdownRef}
    >
      {props.label && (
        <Title2 className={styles.title}>{props.label}</Title2>
      )}

      <TooltipContainer
        disabledMessage={(props.disabled && props.disabledMessage) || ""}
      >
        <DropdownSimple
          triggerContent={(
            <div
              className={cn(styles.inputWrapper, isOpen && styles.inputWrapperOpen)}
              style={{ width: props.inputWidth }}
              // onClick={() => (!props.disabled && setIsOpen(!isOpen))}
            >
              {props.prefix ? (<div className={styles.prefix}>{props.prefix}</div>) : null}
              {(isOpen && (props.searchable || props.searchHandler)) ? (
                <input
                  type="text"
                  className={styles.searchInput}
                  autoFocus
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                />
              ) : (
                ((!value && props.placeholder) ? (
                  <div className={styles.placeholder}>
                    {props.placeholder}
                  </div>
                ) : (
                  props.renderSelectedItem ? (
                    <div className={styles.value}>
                      {props.renderSelectedItem({ item: value })}
                    </div>
                  ) : (
                    SelectedItemComponent ? (
                      <SelectedItemComponent item={value} />
                    ) : (
                      <div className={styles.value}>
                        {(props.labelSelectedItem || (value && props.labelOptionItem) || labelSelectedItemDefault)(value)}
                      </div>
                    )
                  )
                ))
              )}
              <div className={styles.rightSection}>
                {props.suffix ? (<div className={styles.suffix}>{props.suffix}</div>) : null}
                {props.hideChevron ? null : (isOpen ? (
                  <ChevronUp className={styles.chevron} />
                ) : (
                  <ChevronDown className={styles.chevron} />
                ))}
              </div>
            </div>
          )}
          dropdownContent={(
            <div
              className={cn(
                styles.menu,
                styles[`padding-${props.menuPadding ?? 's'}`],
                // styles[`align-${props.dropdownAlign ?? 'start'}`],
              )}
              style={{ width: props.dropdownWidth ?? dropdownWidth }}
            >
              {props.header ?? null}
              <div
                className={styles.menuContent}
                style={{ maxHeight: `calc((${props.menuQuantity ?? 7}) * ${styles.menuItemHeight})` }}
              >
                {items.map((item) => {
                  const itemKey = props.keyExtractor(item);
                  const isSelected = itemKey === props.value;
                  const ItemComponent = props.ItemComponent;
                  return (
                    <DropdownItem
                      key={itemKey}
                      isSelected={isSelected}
                      onClick={() => {
                        handleSelect(item);
                        setIsOpen(false);
                      }}
                    >
                      {(props.renderItem ? (
                        props.renderItem({ item, search: searchString, isSelected, onClose: () => setIsOpen(false) })
                      ) : (ItemComponent ? (
                        <ItemComponent item={item} search={searchString} isSelected={isSelected} />
                      ) : renderItemDefault({
                        item,
                        search: searchString,
                        labelOptionItem: getItemLabel,
                        isSelected,
                      })))}
                    </DropdownItem>
                  );
                })}
              </div>
              {props.isLoading ? (
                <div className={styles.loader}>
                  <Loader />
                </div>
              ) : (props.footer || null)}
            </div>
          )}
          menuAlign={props.dropdownAlign === "end" ? "left" : "right"}
          menuPosition="bottom"
          disabled={props.disabled}
          // menuWidth,
          // fixed
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </TooltipContainer>

      {showFooter ? (
        <div className={styles.footer}>
          {(showError) ? (
            <Title3>{props.errorMessage}</Title3>
          ) : (
            props.description ? (
              <Title3>{props.description}</Title3>
            ) : null
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SelectDropdown;
